/**
 * Admin Layout
 * Main layout wrapper with sidebar navigation
 */

import type { ReactNode } from "react"
import { useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { apiClient } from "../lib/api"
import { getRoleDisplay } from "../lib/utils"

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Change password state
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  })
  const [passwordErrors, setPasswordErrors] = useState<Partial<Record<"currentPassword" | "newPassword" | "confirmPassword" | "root", string>>>({})

  // Base navigation items available to all admin users
  const baseNavItems = [
    { path: "/dashboard", label: "Dashboard" },
    { path: "/organizations", label: "Organizations" },
    { path: "/currency-controls", label: "Currency Controls" },
    { path: "/activity-log", label: "Activity Log" },
  ]

  // Add Admin Users link only for SUPER_ADMIN
  const navItems =
    user?.role === "super_admin" ? [...baseNavItems, { path: "/admin-users", label: "Admin Users" }] : baseNavItems

  const handleLogout = async () => {
    await logout()
    navigate("/login")
  }

  const validatePasswordForm = (): boolean => {
    const errors: Partial<Record<"currentPassword" | "newPassword" | "confirmPassword" | "root", string>> = {}

    if (!passwordForm.currentPassword) {
      errors.currentPassword = "Current password is required"
    }
    if (!passwordForm.newPassword || passwordForm.newPassword.length < 8) {
      errors.newPassword = "New password must be at least 8 characters"
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = "Passwords do not match"
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      errors.newPassword = "New password must be different from current password"
    }

    setPasswordErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validatePasswordForm()) {
      return
    }

    try {
      setIsChangingPassword(true)
      setPasswordErrors({})
      await apiClient.changePassword(passwordForm.currentPassword, passwordForm.newPassword)
      setIsChangePasswordOpen(false)
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" })
      // Show success message (you could add a toast notification here)
      alert("Password changed successfully")
    } catch (err) {
      setPasswordErrors({
        root: err instanceof Error ? err.message : "Failed to change password",
      })
    } finally {
      setIsChangingPassword(false)
    }
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", height: "100vh" }}>
      {/* Sidebar */}
      <aside
        style={{
          width: "280px",
          background: "rgba(15, 23, 42, 0.8)",
          backdropFilter: "blur(40px)",
          borderRight: "1px solid rgba(255, 255, 255, 0.1)",
          color: "white",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 0 60px rgba(0, 0, 0, 0.3)",
          position: "fixed",
          top: 0,
          bottom: 0,
        }}
      >
        {/* Logo/Brand */}
        <div style={{ padding: "1.5rem", borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}>
          <img src="/ofx-logo-light.svg" width="144px" id="logo" alt="OpenFX logo" />
        </div>

        <p style={{ color: "#94a3b8", fontSize: "0.75rem", textAlign: "center", marginTop: "1rem" }}>
          API Sandbox Admin Dashboard
        </p>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: "1.5rem 1rem", overflow: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {navItems.map((item) => {
              const isActive = location.pathname.startsWith(item.path)
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.875rem 1rem",
                    borderRadius: "12px",
                    textDecoration: "none",
                    color: isActive ? "#f1f5f9" : "#cbd5e1",
                    background: isActive
                      ? "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))"
                      : "transparent",
                    border: isActive ? "1px solid rgba(139, 92, 246, 0.3)" : "1px solid transparent",
                    fontSize: "0.9375rem",
                    fontWeight: isActive ? 600 : 500,
                    transition: "all 0.3s ease",
                    boxShadow: isActive ? "0 0 20px rgba(139, 92, 246, 0.2)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                      e.currentTarget.style.color = "#f1f5f9"
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "transparent"
                      e.currentTarget.style.color = "#cbd5e1"
                    }
                  }}
                >
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </div>
        </nav>

        {/* User info */}
        <div style={{ padding: "1.5rem", borderTop: "1px solid rgba(255, 255, 255, 0.1)" }}>
          <div style={{ marginBottom: "1rem" }}>
            <span
              style={{
                display: "flex",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: "#f1f5f9",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {user?.username}

              <span
                className="badge badge-default"
                style={{
                  display: "inline-block",
                  fontSize: "0.75rem",
                }}
              >
                {user ? getRoleDisplay(user.role) : ""}
              </span>
            </span>
            <p style={{ fontSize: "0.8125rem", color: "#94a3b8", marginBottom: "0.5rem" }}>{user?.email}</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <button
              type="button"
              onClick={() => setIsChangePasswordOpen(true)}
              style={{
                width: "100%",
                padding: "0.75rem 1rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                color: "#e2e8f0",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(99, 102, 241, 0.15)"
                e.currentTarget.style.borderColor = "rgba(99, 102, 241, 0.3)"
                e.currentTarget.style.color = "#a78bfa"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)"
                e.currentTarget.style.color = "#e2e8f0"
              }}
            >
              Change Password
            </button>
            <button
              type="button"
              onClick={handleLogout}
              style={{
                width: "100%",
                padding: "0.75rem 1rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                color: "#e2e8f0",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(239, 68, 68, 0.15)"
                e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.3)"
                e.currentTarget.style.color = "#fca5a5"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)"
                e.currentTarget.style.color = "#e2e8f0"
              }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, display: "flex", flexDirection: "column", marginLeft: "280px", overflow: "hidden" }}>
        {/* Header */}
        <header
          style={{
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(40px)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "1.5rem 2rem",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9" }}>
              {navItems.find((item) => item.path === location.pathname)?.label || "Admin"}
            </h1>
            <p style={{ fontSize: "0.875rem", color: "#94a3b8" }}>
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
        </header>

        {/* Page Content */}
        <div style={{ flex: 1, overflow: "auto" }}>{children}</div>
      </main>

      {/* Change Password Modal */}
      {isChangePasswordOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
          onClick={() => {
            setIsChangePasswordOpen(false)
            setPasswordErrors({})
            setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" })
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setIsChangePasswordOpen(false)
              setPasswordErrors({})
              setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" })
            }
          }}
        >
          <div
            className="card"
            style={{ maxWidth: "500px", width: "90%", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
              Change Password
            </h2>
            <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "1.5rem" }}>
              Update your admin account password. Your new password must be at least 8 characters long.
            </p>

            <form onSubmit={handleChangePassword}>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div>
                  <label htmlFor="current-password" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Current Password
                  </label>
                  <input
                    id="current-password"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    placeholder="Enter your current password"
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: `1px solid ${passwordErrors.currentPassword ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.1)"}`,
                      borderRadius: "8px",
                      color: "#f1f5f9",
                      fontSize: "0.9375rem",
                    }}
                  />
                  {passwordErrors.currentPassword && (
                    <p style={{ color: "#f87171", fontSize: "0.75rem", marginTop: "0.25rem" }}>{passwordErrors.currentPassword}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="new-password" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    New Password
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    placeholder="Minimum 8 characters"
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: `1px solid ${passwordErrors.newPassword ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.1)"}`,
                      borderRadius: "8px",
                      color: "#f1f5f9",
                      fontSize: "0.9375rem",
                    }}
                  />
                  {passwordErrors.newPassword && (
                    <p style={{ color: "#f87171", fontSize: "0.75rem", marginTop: "0.25rem" }}>{passwordErrors.newPassword}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirm-password" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Confirm New Password
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="Re-enter new password"
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: `1px solid ${passwordErrors.confirmPassword ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.1)"}`,
                      borderRadius: "8px",
                      color: "#f1f5f9",
                      fontSize: "0.9375rem",
                    }}
                  />
                  {passwordErrors.confirmPassword && (
                    <p style={{ color: "#f87171", fontSize: "0.75rem", marginTop: "0.25rem" }}>{passwordErrors.confirmPassword}</p>
                  )}
                </div>

                {passwordErrors.root && (
                  <div
                    style={{
                      padding: "0.75rem",
                      background: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                      borderRadius: "8px",
                    }}
                  >
                    <p style={{ color: "#f87171", fontSize: "0.875rem" }}>{passwordErrors.root}</p>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => {
                    setIsChangePasswordOpen(false)
                    setPasswordErrors({})
                    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" })
                  }}
                  disabled={isChangingPassword}
                  style={{
                    padding: "0.625rem 1.25rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    color: "#e2e8f0",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    cursor: isChangingPassword ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  style={{
                    padding: "0.625rem 1.25rem",
                    background: isChangingPassword
                      ? "rgba(99, 102, 241, 0.5)"
                      : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    cursor: isChangingPassword ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  {isChangingPassword ? "Changing..." : "Change Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
